// src/pages/WineryPage.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, NavLink } from "react-router-dom";
import { Wine, Calendar, BookOpen, Settings, Plus, Trash2 } from "lucide-react";
import { TopBar, ErrorMsg } from "../components/UI";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

// ── Winery nav tabs ──────────────────────────────────────────────────────────
function WineryNav() {
  const tabs = [
    { to: "/winery",              label: "Profile",      icon: Wine     },
    { to: "/winery/availability", label: "Availability", icon: Calendar },
    { to: "/winery/bookings",     label: "Bookings",     icon: BookOpen },
  ];
  return (
    <div className="flex border-b border-chalk-warm bg-white sticky top-14 z-30">
      {tabs.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} end={to === "/winery"}
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium
             border-b-2 transition-colors
             ${isActive
               ? "border-vine text-vine"
               : "border-transparent text-stone hover:text-slate"}`
          }>
          <Icon size={18} strokeWidth={1.5} />
          {label}
        </NavLink>
      ))}
    </div>
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────────
function WineryProfile() {
  const [winery, setWinery]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [form,    setForm]    = useState({});

  useEffect(() => {
    api.getWinery()
      .then(w => { setWinery(w); setForm(w); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.updateWinery(form);
      setWinery(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return (
    <div className="px-4 pt-6 flex flex-col gap-3">
      {[1,2,3].map(i => (
        <div key={i} className="card animate-pulse">
          <div className="h-4 bg-chalk-warm rounded w-1/4 mb-3" />
          <div className="h-10 bg-chalk-warm rounded" />
        </div>
      ))}
    </div>
  );

  return (
    <form onSubmit={handleSave} className="px-4 pt-5 pb-20 flex flex-col gap-4">
      <ErrorMsg message={error} />

      <div>
        <label className="label">Winery name</label>
        <input className="input" value={form.name || ""}
          onChange={e => set("name", e.target.value)} />
      </div>

      <div>
        <label className="label">Description</label>
        <textarea className="input" rows={3} value={form.description || ""}
          onChange={e => set("description", e.target.value)} />
      </div>

      <div>
        <label className="label">Address</label>
        <input className="input" value={form.address || ""}
          onChange={e => set("address", e.target.value)} />
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label className="label">Latitude</label>
          <input className="input" type="number" step="any"
            value={form.lat || ""}
            onChange={e => set("lat", parseFloat(e.target.value))} />
        </div>
        <div className="flex-1">
          <label className="label">Longitude</label>
          <input className="input" type="number" step="any"
            value={form.lng || ""}
            onChange={e => set("lng", parseFloat(e.target.value))} />
        </div>
      </div>

      <div>
        <label className="label">Price tier</label>
        <div className="flex gap-2">
          {[
            { v: 1, l: "Budget" }, { v: 2, l: "Moderate" },
            { v: 3, l: "Premium" }, { v: 4, l: "Luxury" }
          ].map(({ v, l }) => (
            <button key={v} type="button"
              onClick={() => set("price_tier", v)}
              className={`flex-1 py-2.5 text-xs font-medium rounded-wine border
                          transition-all
                ${form.price_tier === v
                  ? "bg-vine text-chalk border-vine"
                  : "bg-white text-slate border-chalk-warm hover:border-stone/40"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between
                      bg-white border border-chalk-warm rounded-wine px-4 py-3">
        <div>
          <p className="text-sm font-medium text-slate-dark">Organic certified</p>
          <p className="text-xs text-stone">Show organic badge on listings</p>
        </div>
        <button type="button"
          onClick={() => set("organic", !form.organic)}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200
            ${form.organic ? "bg-leaf" : "bg-chalk-warm border border-stone/30"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white
                            shadow-sm transition-transform duration-200
            ${form.organic ? "translate-x-5" : "translate-x-0"}`}/>
        </button>
      </div>

      <button type="submit" disabled={saving} className="btn-primary w-full">
        {saving ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
}

// ── Availability tab ──────────────────────────────────────────────────────────
function WineryAvailability() {
  const today = new Date().toISOString().split("T")[0];
  const [from,     setFrom]     = useState(today);
  const [slots,    setSlots]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [newSlot,  setNewSlot]  = useState({ date: today, slot_time: "10:00", capacity: 10 });

  function loadSlots() {
    setLoading(true);
    const to = new Date(new Date(from).getTime() + 30 * 86400000)
      .toISOString().split("T")[0];
    api.getAvailability(from, to)
      .then(setSlots)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadSlots(); }, [from]);

  async function addSlot() {
    setError("");
    try {
      await api.setAvailability(newSlot);
      loadSlots();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="px-4 pt-5 pb-20 flex flex-col gap-4">
      <ErrorMsg message={error} />

      {/* Add slot */}
      <div className="card">
        <p className="text-sm font-medium text-slate-dark mb-3 flex items-center gap-1.5">
          <Plus size={14} className="text-vine" /> Add availability slot
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="label">Date</label>
              <input type="date" className="input"
                value={newSlot.date}
                min={today}
                onChange={e => setNewSlot(s => ({ ...s, date: e.target.value }))} />
            </div>
            <div className="flex-1">
              <label className="label">Time</label>
              <input type="time" className="input"
                value={newSlot.slot_time}
                onChange={e => setNewSlot(s => ({ ...s, slot_time: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label">Capacity</label>
            <input type="number" className="input" min={1} max={100}
              value={newSlot.capacity}
              onChange={e => setNewSlot(s => ({ ...s, capacity: parseInt(e.target.value) }))} />
          </div>
          <button onClick={addSlot} className="btn-primary w-full">
            Add slot
          </button>
        </div>
      </div>

      {/* Slot list */}
      <div>
        <label className="label mb-2">View from</label>
        <input type="date" className="input mb-3"
          value={from} min={today}
          onChange={e => setFrom(e.target.value)} />

        {loading ? (
          <div className="flex flex-col gap-2">
            {[1,2,3].map(i => (
              <div key={i} className="card animate-pulse h-12" />
            ))}
          </div>
        ) : slots.length === 0 ? (
          <p className="text-sm text-stone text-center py-6">No slots in this period</p>
        ) : (
          <div className="flex flex-col gap-2">
            {slots.map((slot, i) => (
              <div key={i} className="card flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-dark">
                    {slot.date} · {slot.slot_time}
                  </p>
                  <p className="text-xs text-stone mt-0.5">
                    {slot.booked}/{slot.capacity} booked
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-chalk-warm rounded-full overflow-hidden">
                    <div className="h-full bg-vine rounded-full"
                      style={{ width: `${(slot.booked/slot.capacity)*100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bookings tab ──────────────────────────────────────────────────────────────
function WineryBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  useEffect(() => {
    api.getBookings()
      .then(setBookings)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const STATUS_STYLES = {
    pending:   "bg-chalk-warm text-slate",
    confirmed: "bg-leaf/10 text-leaf",
    cancelled: "bg-red-50 text-red-600",
    completed: "bg-chalk-warm text-stone",
  };

  return (
    <div className="px-4 pt-5 pb-20 flex flex-col gap-3">
      <ErrorMsg message={error} />

      {loading ? (
        [1,2,3].map(i => <div key={i} className="card animate-pulse h-20" />)
      ) : bookings.length === 0 ? (
        <p className="text-sm text-stone text-center py-10">No bookings yet</p>
      ) : bookings.map((b, i) => (
        <div key={i} className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-dark">
                {b.visit_date} · {b.slot_time}
              </p>
              <p className="text-xs text-stone mt-0.5">
                {b.party_size} {b.party_size === 1 ? "guest" : "guests"}
              </p>
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                              ${STATUS_STYLES[b.status] || "bg-chalk-warm text-slate"}`}>
              {b.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Root winery page ─────────────────────────────────────────────────────────
export default function WineryPage() {
  const { user } = useAuth("winery_owner");
  const role = user?.role || null;
  const navigate = useNavigate();

  if (role !== "winery_owner") {
    return (
      <div className="min-h-screen pb-20">
        <TopBar title="Winery portal" back={() => navigate("/account")} />
        <div className="px-4 pt-6">
          <div className="card text-center py-8">
            <p className="font-display text-lg font-semibold text-slate-dark">
              Access restricted
            </p>
            <p className="text-sm text-stone mt-1">
              This portal is for registered winery owners.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <TopBar title="Winery portal" back={() => navigate("/account")} />
      <WineryNav />
      <Routes>
        <Route index element={<WineryProfile />} />
        <Route path="availability" element={<WineryAvailability />} />
        <Route path="bookings"     element={<WineryBookings />} />
      </Routes>
    </div>
  );
}
